import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import { useDispositionQuery } from "../../../Services/ticket/CreateTicket";
import { USER_TEAM_ID } from "../../../constants";
import { toast } from "react-toastify";
import {
  useUseUpdateYoutubeConfigurationAiMutationMutation,
  useUseUpdateYoutubeConfigurationMutationMutation,
  useUseUpdateYoutubeConfigurationVectorMutationMutation
} from "../../../Services/instagram/Instagram";



interface EditConfigurationModalProps {
  closeModal: () => void;
  configurationData: any;
  refetchYoutubeData?: () => void;
  refetchVectorData?: () => void;
  refetchAiData?: () => void;
}

const EditConfigurationModal: React.FC<EditConfigurationModalProps> = ({
  closeModal,
  configurationData,
  refetchYoutubeData,
  refetchVectorData,
  refetchAiData,
}) => {
  const steps = [
    'YouTube Configuration',
    'Update Configuration Vector',
    'AI Configuration',
  ];
  const teamId = Number(localStorage.getItem(USER_TEAM_ID)) || 0;
  const [triggerUpdateYoutubeConfiguration, { isLoading: isUpdatingYoutube }] = useUseUpdateYoutubeConfigurationMutationMutation();
  const [triggerUpdateYoutubeConfigurationVector] = useUseUpdateYoutubeConfigurationVectorMutationMutation();
  const [triggerUpdateYoutubeConfigurationAi] = useUseUpdateYoutubeConfigurationAiMutationMutation();


  const [step, setStep] = useState(0);
  // Track the hierarchy of selected dispositions
  const [dispositionPath, setDispositionPath] = useState<number[]>([]);
  const [currentLevels, setCurrentLevels] = useState<any[]>([]);

  // Fetch disposition data
  const { data: dispositionData, isLoading: isDispositionLoading } = useDispositionQuery(teamId);

  const initialValues = {
    // Step 1 fields
    id: configurationData?.id || '',
    channel_id: configurationData?.channel_id || '',
    client_id: configurationData?.client_id || '',
    client_secret: configurationData?.client_secret || '',

    token: configurationData?.token || '',
    refresh_token: configurationData?.refresh_token || '',
    token_expiry: configurationData?.token_expiry
      ? new Date(configurationData.token_expiry).toISOString().slice(0, 16)
      : '',
    reply_frequency: configurationData?.reply_frequency || '',
    auto_ticket: configurationData?.auto_ticket || false,
    auto_reply: configurationData?.auto_reply || false,
    ticket_msg: configurationData?.ticket_msg || '',
    team: teamId,
    // Only store the last disposition ID in the form values
    disposition: configurationData?.disposition?.toString() || '',
    // Initialize disposition_ids as an empty array - will be populated when user makes a selection
    disposition_ids: configurationData?.disposition ? [configurationData.disposition] : [],

    // Step 2 fields
    vector_api_url: configurationData?.vector_config?.vector_api_url || '',
    collection_name: configurationData?.vector_config?.collection_name || '',
    api_key: configurationData?.ai_config?.api_key || '',
  };
  console.log(configurationData, 'configurationData')
  const validationSchemas = [
    // Step 1: All fields required except ticket_msg, disposition
    Yup.object().shape({
      channel_id: Yup.string().required('Channel ID is required'),
      client_id: Yup.string().required('Client ID is required'),
      client_secret: Yup.string().required('Client Secret is required'),

      token: Yup.string().required('Token is required'),
      refresh_token: Yup.string().required('Refresh Token is required'),
      token_expiry: Yup.string().required('Token Expiry is required'),
      reply_frequency: Yup.number().typeError('Reply Frequency must be a number').required('Reply Frequency is required'),
      auto_ticket: Yup.boolean(),
      ticket_msg: Yup.string()
        .nullable()
        .when('auto_ticket', {
          is: true,
          then: (schema) => schema.required('Ticket Message is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      disposition: Yup.string()
        .nullable()
        .when('auto_ticket', {
          is: true,
          then: (schema) => schema.required('Disposition is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
      disposition_ids: Yup.array()
        .when('auto_ticket', {
          is: true,
          then: (schema) => schema.min(1, 'Please select a disposition'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
    // Step 2: vector config
    Yup.object().shape({
      vector_api_url: Yup.string().required('Vector API URL is required'),
      collection_name: Yup.string().required('Collection Name is required'),
    }),
    // Step 3: AI Configuration
    Yup.object().shape({
      api_key: Yup.string().required('API Key is required')
    }),
  ];

  // Initialize disposition levels when data loads or changes
  useEffect(() => {

    if (dispositionData) {
      // Set first level options
      setCurrentLevels([{ level: 0, options: dispositionData }]);

      // If we have a selected disposition, find its path
      if (configurationData?.disposition) {
        console.log('Finding path for disposition:', configurationData.disposition);

        const findPath = (items: any[], targetId: number, path: number[] = [], depth = 0): number[] | null => {
          console.log(`Searching at depth ${depth}`, { items: items.map(i => ({ id: i.id, topic: i.topic })) });

          for (const item of items) {
            console.log(`Checking item ${item.id} (${item.topic})`);
            if (item.id === targetId) {
              console.log('Found matching item!');
              return [...path, item.id];
            }
            if (item.sub_topics?.length) {
              console.log(`Item has ${item.sub_topics.length} sub-topics, searching deeper...`);
              const found = findPath(item.sub_topics, targetId, [...path, item.id], depth + 1);
              if (found) return found;
            }
          }
          console.log('No match found at this level');
          return null;
        };

        const dispositionId = parseInt(configurationData.disposition);
        console.log('Looking for disposition ID:', dispositionId);

        const path = findPath(dispositionData, dispositionId);
        console.log('Found path:', path);

        if (path) {
          console.log('Setting disposition path:', path);
          setDispositionPath(path);

          // Build the levels based on the path
          const levels = [];
          let currentOptions = dispositionData;

          for (let i = 0; i < path.length; i++) {
            const currentId = path[i];
            console.log(`Processing level ${i}, currentId: ${currentId}`);

            levels.push({
              level: i,
              options: currentOptions,
              selected: currentId
            });

            const currentItem = currentOptions.find(opt => opt.id === currentId);
            console.log(`Current item at level ${i}:`, currentItem);

            if (currentItem?.sub_topics?.length) {
              console.log(`Found ${currentItem.sub_topics.length} sub-topics`);
              currentOptions = currentItem.sub_topics;
            } else {
              console.log('No more sub-topics, stopping');
              break;
            }
          }

          console.log('Setting levels:', levels);
          setCurrentLevels(levels);
        } else {
          console.warn('Could not find path for disposition:', configurationData.disposition);
        }
      }
    }
  }, [dispositionData, configurationData]);

  // Handle disposition selection at a specific level
  const handleDispositionSelect = (level: number, value: string) => {
    const selectedId = parseInt(value);

    // Update the path up to the selected level
    const newPath = [...dispositionPath];
    newPath[level] = selectedId;
    newPath.length = level + 1; // Remove any deeper selections

    setDispositionPath(newPath);

    // Find the selected item to see if it has children
    let currentOptions = dispositionData || [];
    const newLevels = [];

    // Rebuild levels up to the selected one
    for (let i = 0; i <= level; i++) {
      newLevels.push({
        level: i,
        options: i === 0 ? dispositionData : currentOptions,
        selected: newPath[i]
      });

      if (i < level) {
        const currentItem = currentOptions.find(opt => opt.id === newPath[i]);
        currentOptions = currentItem?.sub_topics || [];
      }
    }

    // If the selected item has sub-topics, add a new level
    const selectedItem = currentOptions.find(opt => opt.id === selectedId);
    if (selectedItem?.sub_topics?.length) {
      newLevels.push({
        level: level + 1,
        options: selectedItem.sub_topics,
        selected: undefined
      });
    }

    setCurrentLevels(newLevels);

    // Update the form with the leaf node (last in path)
    if (selectedItem && (!selectedItem.sub_topics || selectedItem.sub_topics.length === 0)) {
      formik.setFieldValue('disposition', selectedId);
      formik.setFieldValue('disposition_ids', newPath);
    } else {
      formik.setFieldValue('disposition', '');
      formik.setFieldValue('disposition_ids', newPath);
    }
  };

  // Render disposition dropdowns
  const renderDispositionDropdowns = () => {
    if (isDispositionLoading) {
      return (
        <div className="d-flex align-items-center">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span>Loading dispositions...</span>
        </div>
      );
    }

    return (
      <div className="disposition-hierarchy">
        {currentLevels.map(({ level, options, selected }) => {
          console.log(`Rendering level ${level}`, { selected, options: options?.map(o => ({ id: o.id, topic: o.topic })) });

          return (
            <div key={`level-${level}`} className="form-group mb-3">
              <label className="required">
                {level === 0 ? 'Disposition' : `Sub-disposition ${level}`}
              </label>
              <select
                className={clsx("form-select mb-2", {
                  "is-invalid": formik.touched.disposition && formik.errors.disposition && level === currentLevels.length - 1,
                })}
                value={selected || ''}
                onChange={(e) => handleDispositionSelect(level, e.target.value)}
              >
                <option value="">Select {level === 0 ? 'Disposition' : `Sub-disposition ${level + 1}`}</option>
                {options?.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.topic || option.name}
                  </option>
                ))}
              </select>
              {formik.touched.disposition &&
                formik.errors.disposition &&
                level === currentLevels.length - 1 && (
                  <div className="invalid-feedback">{formik.errors.disposition}</div>
                )}
            </div>
          );
        })}
      </div>
    );
  };

  const formik = useFormik({
    initialValues,
    validationSchema: validationSchemas[step],
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
    validateOnMount: false,
    isInitialValid: false,
    onSubmit: async (values) => {
      if (step < steps.length - 1) {
        setStep((s) => s + 1);
      } else {
        // Handle the final step submission (AI Configuration)
        try {
          const aiConfigData = {
            id: configurationData.ai_config?.id,
            team: teamId,
            api_key: values.api_key
          };

          const result = await triggerUpdateYoutubeConfigurationAi({ body: aiConfigData, id: configurationData.ai_config?.id });
          if ('error' in result && result.error) {
            throw new Error('Failed to update AI configuration');
          }

          // Show success message for AI configuration
          refetchAiData?.();
          toast.success('AI Configuration updated successfully!');
          closeModal();
        } catch (error) {
          console.error('Error updating AI configuration:', error);
          toast.error('Failed to update AI configuration. Please try again.');
        }
      }
    },
  });

  const handleNext = async () => {
    try {
      const valid = await formik.validateForm();
      formik.setTouched({
        ...Object.keys(formik.values).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {}),
      });

      if (Object.keys(valid).length > 0) {
        throw { inner: Object.entries(valid).map(([path, message]) => ({ path, message })) };
      }

      if (step === 0) {
        // First step - update YouTube configuration
        const firstStepData = {
          id: formik.values.id, // Keep the id for the update operation
          channel_id: formik.values.channel_id,
          client_id: formik.values.client_id,
          client_secret: formik.values.client_secret,
          token: formik.values.token,
          refresh_token: formik.values.refresh_token,
          token_expiry: formik.values.token_expiry,
          reply_frequency: formik.values.reply_frequency,
          auto_ticket: formik.values.auto_ticket,
          ticket_msg: formik.values.ticket_msg,
          team: teamId,
          auto_reply: formik.values.auto_reply,
          // Get the last selected ID from disposition_ids array
          disposition: formik.values.disposition_ids?.length > 0
            ? formik.values.disposition_ids[formik.values.disposition_ids.length - 1]
            : '',
          api_key: formik.values.api_key
        };

        console.log('Submitting YouTube config with disposition:', firstStepData.disposition);

        const result = await triggerUpdateYoutubeConfiguration({
          id: firstStepData.id,
          body: firstStepData
        });

        if ('error' in result && result.error) {
          throw new Error('Failed to update YouTube configuration');
        }

        toast.success('YouTube Configuration updated successfully!');
        refetchYoutubeData?.();
        setStep(step + 1);
      } else if (step === 1) {
        // Second step - update vector configuration
        const vectorConfigData = {
          vector_api_url: formik.values.vector_api_url,
          collection_name: formik.values.collection_name,
          team: teamId
        };

        const result = await triggerUpdateYoutubeConfigurationVector({
          id: configurationData.vector_config?.id,
          body: vectorConfigData
        });

        if ('error' in result && result.error) {
          throw new Error('Failed to update Vector configuration');
        }

        toast.success('Vector Configuration updated successfully!');
        refetchVectorData?.();
        setStep(step + 1);
      } else if (step === 2) {
        // Third step - handled by formik's onSubmit
        await formik.submitForm();
      }
    } catch (error) {
      // Handle validation errors
      if (error.inner) {
        const errorsObj = {};
        error.inner.forEach((err: { path: string; message: string }) => {
          errorsObj[err.path] = err.message;
        });
        formik.setErrors(errorsObj);

        // Mark all fields as touched to show validation messages
        const touched = {};
        Object.keys(formik.values).forEach(key => {
          touched[key] = true;
        });
        formik.setTouched(touched);
      } else {
        console.error('Error:', error);
        toast.error(error.message || 'An error occurred');
      }
    }
  };

  const handleBack = () => {
    // Clear any previous errors when going back
    formik.setErrors({});
    setStep((s) => s - 1);
  };

  const handleCancel = () => {
    // Reset form and close modal
    formik.resetForm();
    closeModal();
  };

  return (
    <div>
      <h4 className="text-center">Edit YouTube Configuration</h4>
      <div className="mb-3 text-center">
        <span>Step {step + 1} of {steps.length}: <b>{steps[step]}</b></span>
      </div>
      <form onSubmit={formik.handleSubmit}>
        {step === 0 && (
          <>
            <div className="form-group mb-3">
              <label className="required">Channel ID</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.channel_id && formik.errors.channel_id })}
                name="channel_id" value={formik.values.channel_id} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.channel_id && formik.errors.channel_id && <div className="invalid-feedback">{formik.errors.channel_id}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Client ID</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.client_id && formik.errors.client_id })}
                name="client_id" value={formik.values.client_id} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.client_id && formik.errors.client_id && <div className="invalid-feedback">{formik.errors.client_id}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Client secret</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.client_secret && formik.errors.client_secret })}
                name="client_secret" value={formik.values.client_secret} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.client_secret && formik.errors.client_secret && <div className="invalid-feedback">{formik.errors.client_secret}</div>}
            </div>

            <div className="form-group mb-3">
              <label className="required">Token</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.token && formik.errors.token })}
                name="token" value={formik.values.token} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.token && formik.errors.token && <div className="invalid-feedback">{formik.errors.token}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Refresh token</label>
              <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.refresh_token && formik.errors.refresh_token })}
                name="refresh_token" value={formik.values.refresh_token} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.refresh_token && formik.errors.refresh_token && <div className="invalid-feedback">{formik.errors.refresh_token}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Token expiry</label>
              <input type="datetime-local" className={clsx("form-control", { "is-invalid": formik.touched.token_expiry && formik.errors.token_expiry })}
                name="token_expiry" value={formik.values.token_expiry} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.token_expiry && formik.errors.token_expiry && <div className="invalid-feedback">{formik.errors.token_expiry}</div>}
            </div>
            <div className="form-group mb-3">
              <label className="required">Reply frequency</label>
              <input type="number" className={clsx("form-control", { "is-invalid": formik.touched.reply_frequency && formik.errors.reply_frequency })}
                name="reply_frequency" value={formik.values.reply_frequency} onChange={formik.handleChange} onBlur={formik.handleBlur} />
              {formik.touched.reply_frequency && formik.errors.reply_frequency && <div className="invalid-feedback">{formik.errors.reply_frequency}</div>}
            </div>
            <div className="form-group mb-3 d-flex align-items-center">
              <label className="mb-0">Auto ticket</label>
              <input type="checkbox" className="form-check-input ms-2" name="auto_ticket" checked={formik.values.auto_ticket} onChange={formik.handleChange} />
              <label className="mb-0 ms-4">Auto reply</label>
              <input type="checkbox" className="form-check-input ms-2" name="auto_reply" checked={formik.values.auto_reply} onChange={formik.handleChange} />
            </div>
            {formik.values.auto_ticket && (
              <>
                <div className="form-group mb-3">
                  <label className="required">Ticket Message</label>
                  <input type="text" className={clsx("form-control", { "is-invalid": formik.touched.ticket_msg && formik.errors.ticket_msg })}
                    name="ticket_msg" value={formik.values.ticket_msg} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                  {formik.touched.ticket_msg && formik.errors.ticket_msg && <div className="invalid-feedback">{formik.errors.ticket_msg}</div>}
                </div>
                {renderDispositionDropdowns()}
              </>
            )}

          </>
        )}
        {step === 1 && (
          <>
            <div className="form-group mb-3">
              <label className="required">Vector API URL</label>
              <input
                type="text"
                className={clsx("form-control", { "is-invalid": formik.touched.vector_api_url && formik.errors.vector_api_url })}
                name="vector_api_url"
                value={formik.values.vector_api_url}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.vector_api_url && formik.errors.vector_api_url && (
                <div className="invalid-feedback">{formik.errors.vector_api_url}</div>
              )}
            </div>
            <div className="form-group mb-3">
              <label className="required">Collection name</label>
              <input
                type="text"
                className={clsx("form-control", { "is-invalid": formik.touched.collection_name && formik.errors.collection_name })}
                name="collection_name"
                value={formik.values.collection_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.collection_name && formik.errors.collection_name && (
                <div className="invalid-feedback">{formik.errors.collection_name}</div>
              )}
            </div>
            <input type="hidden" name="team" value={formik.values.team} />
          </>
        )}
        {step === 2 && (
          <>
            <div className="form-group mb-3">
              <label className="required">API key</label>
              <input
                type="password"
                className={clsx("form-control", { "is-invalid": formik.touched.api_key && formik.errors.api_key })}
                name="api_key"
                value={formik.values.api_key}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter your AI API key"
              />
              {formik.touched.api_key && formik.errors.api_key && (
                <div className="invalid-feedback">{formik.errors.api_key}</div>
              )}
            </div>
          </>
        )}
        <div className="d-flex justify-content-end mt-6">
          <button type="button" className="btn btn-secondary me-2" onClick={handleCancel}>Cancel</button>
          {step > 0 && (
            <button type="button" className="btn btn-outline-primary me-2" onClick={handleBack}>
              Back
            </button>
          )}
          {step < steps.length - 1 ? (
            <button type="button" className="btn btn-primary" onClick={handleNext}>
              Next
            </button>
          ) : (
            <button type="submit" className="btn btn-primary">
              Update
            </button>
          )}
        </div>
      </form>
    </div>
  );


};

export default EditConfigurationModal;
